import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';

interface UserGuideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function UserGuideModal({ open, onOpenChange }: UserGuideModalProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('userGuideDontShowAgain');
    if (saved === 'true') {
      setDontShowAgain(true);
    }
  }, []);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem('userGuideDontShowAgain', 'true');
    }
    onOpenChange(false);
  };

  // 오버레이 클릭/Esc/우측 상단 기본 닫기 버튼(DialogContent 내장) 등 Dialog가
  // 자체적으로 닫힐 때도 handleClose와 동일하게 dontShowAgain을 반영한다.
  const handleOpenChange = (next: boolean) => {
    if (!next && dontShowAgain) {
      localStorage.setItem('userGuideDontShowAgain', 'true');
    }
    onOpenChange(next);
  };

  const goToGuide = () => {
    if (dontShowAgain) {
      localStorage.setItem('userGuideDontShowAgain', 'true');
    }
    onOpenChange(false);
    window.location.href = '/dashboard/user-guide';
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">ALT Generation 이용가이드</DialogTitle>
          <DialogDescription className="text-base">
            이미지ALT 자동 생성 서비스를 시작하는 방법을 안내합니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* 빠른 시작 */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-blue-600" />
              빠른 시작
            </h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                  <strong>1. 컨텍스트 프로필 설정</strong> - 웹사이트 주제와 키워드를 설정하여 더 정확한 대체 텍스트 생성
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                  <strong>2. 이미지 추가</strong> - 웹사이트 URL, 이미지 URL, 또는 직접 업로드 방식으로 이미지 추가
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                  <strong>3. 대체 텍스트 생성</strong> - AI가 자동으로 이미지에 맞는 대체 텍스트 생성
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                  <strong>4. 결과 다운로드</strong> - 엑셀 파일로 다운로드하여 웹사이트에 적용
                </p>
              </div>
            </div>
          </div>

          {/* 주요 기능 바로가기 */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              주요 기능 바로가기
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/dashboard/crawler/website" onClick={handleClose}>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  웹사이트 크롤러
                </Button>
              </Link>
              <Link to="/dashboard/crawler/image-url" onClick={handleClose}>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  이미지 URL 크롤러
                </Button>
              </Link>
              <Link to="/dashboard/crawler/upload" onClick={handleClose}>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  이미지 업로드
                </Button>
              </Link>
              <Link to="/dashboard/profile/context" onClick={handleClose}>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  컨텍스트 프로필
                </Button>
              </Link>
            </div>
          </div>

          {/* 다음에 보기 옵션 */}
          <div className="flex items-center space-x-2 pt-4 border-t">
            <input
              type="checkbox"
              id="dont-show-again"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
            />
            <label
              htmlFor="dont-show-again"
              className="text-sm text-gray-600 cursor-pointer select-none"
            >
              다음에 이 가이드를 표시하지 않음
            </label>
          </div>
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            닫기
          </Button>
          <Button onClick={goToGuide}>
            자세히 보기
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
